import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

function fmt(n, d = 2) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toFixed(d);
}

function pick(obj, ...keys) {
  return keys.reduce((o, k) => (obj?.[k] != null ? obj[k] : o), undefined);
}

function getUserDetails(data) {
  const u = data?.user || data?.profile || data;

  const name = pick(u, "name", "fullName", "displayName") ?? "—";
  const designation =
    pick(u, "designation", "title") ??
    pick(u?.role, "roleName") ??
    pick(data, "designation") ??
    "—";

  // try nested role object, then flat role name
  const role =
    pick(u?.role, "roleName") ??
    pick(u, "roleName", "role") ??
    pick(data, "role") ??
    "—";

  const email = pick(u, "emailId", "email") ?? "—";
  const phone =
    pick(u, "phone", "mobile", "contactNumber", "phoneNumber") ?? "—";

  return { name, designation, role, email, phone };
}

function isReal(v) {
  return v && v !== "—";
}



export default function SingleUserPanel({ data, include, onBack }) {
  // Build chart data safely
  const monthlyChartData = useMemo(() => {
    if (!data?.activity?.byMonth) return [];
    const months = new Set();
    const mod = data.activity.byMonth;
    Object.values(mod).forEach((obj) =>
      Object.keys(obj || {}).forEach((m) => months.add(m))
    );
    const sorted = Array.from(months).sort();
    return sorted.map((k) => ({
      month: k,
      UaUC: mod?.UaUC?.[k] || 0,
      TbtMeeting: mod?.TbtMeeting?.[k] || 0,
      Induction: mod?.Induction?.[k] || 0,
      WorkPermit: mod?.WorkPermit?.[k] || 0,
    }));
  }, [data]);

  const dailySpark = useMemo(() => {
    if (!data?.activity?.daily) return [];
    const entries = Object.entries(data.activity.daily).sort(([a], [b]) =>
      a > b ? 1 : -1
    );
    return entries.map(([date, count]) => ({ date, count }));
  }, [data]);

  return (
    <section className="spd__grid">
      {/* Left Column */}
      <div className="spd__col">
        <button
          className="spd__btn spd__btn--ghost spd__btn--sm"
          onClick={onBack}
        >
          ← Back to list
        </button>

        {/* User Details */}
        <div className="spd__card">
          <h3 className="spd__cardTitle">User Details</h3>
          {(() => {
            const u = getUserDetails(data);
            return (
              <div className="spd__details">
                <div className="spd__detail">
                  <span className="spd__detailKey">Name</span>
                  <span className="spd__detailVal">{u.name}</span>
                </div>
                <div className="spd__detail">
                  <span className="spd__detailKey">Designation</span>
                  <span className="spd__detailVal">{u.designation}</span>
                </div>
                <div className="spd__detail">
                  <span className="spd__detailKey">Role</span>
                  <span className="spd__detailVal">{u.role}</span>
                </div>
                <div className="spd__detail">
                  <span className="spd__detailKey">Phone</span>
                  <span className="spd__detailVal">
                    {isReal(u.phone) ? (
                      <a href={`tel:${u.phone}`}>{u.phone}</a>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
                <div className="spd__detail">
                  <span className="spd__detailKey">Email</span>
                  <span className="spd__detailVal">
                    {isReal(u.email) ? (
                      <a href={`mailto:${u.email}`}>{u.email}</a>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="spd__card">
          <h3 className="spd__cardTitle">Overview</h3>
          <div className="spd__kpiGrid">
            <KPI label="Score" value={fmt(data?.percentage)} />
            <KPI label="Participation" value={fmt(data?.participationRate)} />
            <KPI label="Active Perf." value={fmt(data?.activePerformance)} />
            <KPI
              label="Streak (days)"
              value={data?.activity?.streakDays ?? "—"}
            />
          </div>
        </div>

        <div className="spd__card">
          <h3 className="spd__cardTitle">Module Breakdown</h3>
          <div className="spd__moduleList">
            {data?.modules?.map((m) => (
              <div key={m.module} className="spd__moduleRow">
                <div className="spd__moduleName">
                  <span className="spd__dot" />
                  <span>{m.module}</span>
                </div>
                <div className="spd__moduleScore">{fmt(m.score)}%</div>
              </div>
            ))}
          </div>
        </div>

        <div className="spd__card">
          <h3 className="spd__cardTitle">Recent Activity</h3>
          <div className="spd__kpiGrid spd__kpiGrid--sm">
            <KPI
              label="Last 30 actions"
              value={data?.activity?.last30 ?? "—"}
            />
            <KPI
              label="Last submitted"
              value={
                data?.activity?.lastSubmittedAt
                  ? new Date(data.activity.lastSubmittedAt).toLocaleString()
                  : "—"
              }
            />
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="spd__col spd__col--wide">
        {include === "full" && monthlyChartData.length > 0 && (
          <div className="spd__card">
            <h3 className="spd__cardTitle">Monthly Submissions by Module</h3>
            <div className="spd__chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="UaUC" stackId="a" />
                  <Bar dataKey="TbtMeeting" stackId="a" />
                  <Bar dataKey="Induction" stackId="a" />
                  <Bar dataKey="WorkPermit" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {include === "full" && dailySpark.length > 1 && (
          <div className="spd__card">
            <h3 className="spd__cardTitle">Daily Activity (All Modules)</h3>
            <div className="spd__chart spd__chart--sm">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailySpark}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" hide />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* Reusable small KPI block */
function KPI({ label, value }) {
  return (
    <div className="spd__kpi">
      <div className="spd__kpiLabel">{label}</div>
      <div className="spd__kpiValue">{value}</div>
    </div>
  );
}
